import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Eye, EyeOff, PhoneCall } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDuration, num, pct, scoreTone } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, Delta, EmptyState, Loading, PageHeader, Pill, RangePicker, ScoreBadge, Section, useRange } from "@/components/common";
import { cn } from "@/lib/utils";

const COLORS = ["#3b82f6", "#06b6d4", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444"];
const DIMS = [
  ["compliance", "Compliance"],
  ["discovery", "Discovery"],
  ["solution_framing", "Solution"],
  ["closing_next_steps", "Closing"],
  ["empathy_tone", "Empathy"],
];

export default function Agents() {
  const [range, setRange] = useRange(30);
  const params = { date_from: range.from, date_to: range.to };
  const agents = useQuery({ queryKey: ["agents", params], queryFn: () => api.get("/analytics/agents", { params }).then((r) => r.data) });
  const trends = useQuery({ queryKey: ["trends-week", params], queryFn: () => api.get("/analytics/trends", { params: { ...params, interval: (new Date(range.to) - new Date(range.from)) / 864e5 > 45 ? "week" : "day" } }).then((r) => r.data) });
  const [sort, setSort] = useState("avg_score");

  const rows = useMemo(() => {
    const list = [...(agents.data || [])];
    list.sort((a, b) => (b[sort] ?? -1) - (a[sort] ?? -1));
    return list;
  }, [agents.data, sort]);

  const dimData = useMemo(() => DIMS.map(([k, label]) => ({ dim: label, ...Object.fromEntries((agents.data || []).filter((a) => a.avg_score != null).slice(0, 6).map((a) => [a.name, a[k]])) })), [agents.data]);
  const scoredAgents = (agents.data || []).filter((a) => a.avg_score != null).slice(0, 6).map((a) => a.name);

  const th = (key, label, right = true) => (
    <TableHead key={key} className={cn("text-[11px] font-mono uppercase tracking-wider h-9 cursor-pointer select-none hover:text-slate-200", right && "text-right", sort === key ? "text-blue-300" : "text-slate-500")} onClick={() => setSort(key)} data-testid={`agents-sort-${key}`}>
      {label}
    </TableHead>
  );

  return (
    <>
      <PageHeader eyebrow="Team analytics" title="Agent performance" description="Benchmarks across every OpenPhone user — volume, talk time, AI coaching scores by dimension, SMS SLA alerts and anomalies. Click a column to sort." actions={<RangePicker range={range} onChange={setRange} />} testId="agents-header" />

      <Section padded={false} testId="agents-table">
        {agents.isLoading ? (
          <Loading />
        ) : rows.length ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1f293d] hover:bg-transparent">
                  <TableHead className="text-[11px] font-mono uppercase tracking-wider text-slate-500 h-9">Agent</TableHead>
                  {th("calls", "Calls")}
                  {th("inbound", "In / Out")}
                  {th("talk_seconds", "Talk time")}
                  {th("avg_duration", "Avg len")}
                  {th("analyzed", "Reviewed")}
                  {th("avg_score", "Avg score")}
                  {th("below_threshold", "Below")}
                  {th("positive", "Positive")}
                  {th("sms_alerts", "SMS alerts")}
                  {th("unrecorded", "Not recorded")}
                  {th("anomalies", "Anomalies")}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.agent_user_id} className="border-[#1f293d] row-hover" data-testid={`agent-row-${a.agent_user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar name={a.name} url={a.picture_url} />
                        <div>
                          <Link to={`/agents/${a.agent_user_id}`} className="text-sm text-slate-100 hover:text-blue-300 font-medium" data-testid={`agent-scorecard-link-${a.agent_user_id}`}>{a.name}</Link>
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono">
                            {a.monitored ? <span className="inline-flex items-center gap-1 text-violet-300"><Eye className="h-3 w-3" /> AI monitored</span> : <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> not monitored</span>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-200">{num(a.calls)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-400">{a.inbound} / {a.outbound}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300">{fmtDuration(a.talk_seconds)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300">{fmtDuration(a.avg_duration)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300">{a.analyzed}</TableCell>
                    <TableCell className="text-right"><div className="inline-flex items-center gap-2"><Delta value={a.score_delta} /><ScoreBadge score={a.avg_score} size="sm" /></div></TableCell>
                    <TableCell className="text-right font-mono text-xs"><span className={a.below_threshold ? "text-red-300" : "text-slate-500"}>{a.below_threshold}</span></TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300">{a.analyzed ? `${pct(a.positive, a.analyzed)}%` : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs"><span className={a.sms_alerts ? "text-amber-300" : "text-slate-500"}>{a.sms_alerts}</span></TableCell>
                    <TableCell className="text-right font-mono text-xs"><span className={a.unrecorded ? "text-red-300" : "text-slate-500"} data-testid={`agent-unrecorded-${a.agent_user_id}`}>{a.unrecorded ?? 0}</span></TableCell>
                    <TableCell className="text-right"><span className={cn("font-mono text-xs", a.anomalies ? "text-red-300" : "text-slate-500")}>{a.anomalies}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState icon={PhoneCall} title="No agent activity in range" hint="Widen the date range or run a sync." />
        )}
      </Section>

      <div className="grid xl:grid-cols-2 gap-4">
        <Section title="Coaching score trajectory" eyebrow="Top agents by reviewed calls" testId="agents-trend-chart">
          {trends.data?.agent_series?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trends.data.agent_series} margin={{ left: -15, right: 10, top: 10 }}>
                <CartesianGrid stroke="#1f293d" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #2d3d58" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {trends.data.agents.map((a, i) => (
                  <Area key={a} type="monotone" dataKey={a} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.08} strokeWidth={2} connectNulls dot={{ r: 2 }} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="No scored calls yet" hint="Scores appear once AI reviews complete for monitored agents." />
          )}
        </Section>
        <Section title="Dimension breakdown" eyebrow="Average by coaching dimension" testId="agents-dimension-chart">
          {scoredAgents.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dimData} margin={{ left: -15, right: 10, top: 10 }}>
                <CartesianGrid stroke="#1f293d" vertical={false} />
                <XAxis dataKey="dim" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#111827", border: "1px solid #2d3d58" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {scoredAgents.map((a, i) => <Bar key={a} dataKey={a} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState title="No dimension data" hint="Compliance, discovery, solution framing, closing and empathy scores will show here." />
          )}
        </Section>
      </div>

      {rows.some((a) => a.avg_score != null) && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agent-cards">
          {rows.filter((a) => a.avg_score != null).map((a) => (
            <div key={a.agent_user_id} className="panel panel-hover p-4 fade-up">
              <div className="flex items-center justify-between">
                <Link to={`/agents/${a.agent_user_id}`} className="flex items-center gap-2 hover:text-blue-300"><Avatar name={a.name} url={a.picture_url} /><span className="text-sm font-semibold text-slate-100">{a.name}</span></Link>
                <ScoreBadge score={a.avg_score} />
              </div>
              <div className="mt-3 space-y-1.5">
                {DIMS.map(([k, label]) => (
                  <div key={k} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-slate-400">{label}</span>
                    <div className="flex-1 h-1.5 bg-[#161f30] rounded"><div className="h-1.5 rounded" style={{ width: `${a[k] || 0}%`, background: scoreTone(a[k]).hex }} /></div>
                    <span className="font-mono w-8 text-right text-slate-300">{a[k] ?? "—"}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-1.5">
                <Pill tone={a.below_threshold ? "red" : "emerald"}>{a.below_threshold} below threshold</Pill>
                <Pill>{a.feedback_sent} coaching emails</Pill>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
